import React, { useEffect } from "react";
import NotificationCard from "../components/notifications/NotificationCard";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  getMyOrganization,
  getReminderList,
  getReminderListRequest,
} from "@src/redux/index";
import { Spin } from "antd";

interface Notification {
  title: string;
  emails: string[];
  schedule: string;
  item?: any;
}

const Notifications: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { result: reminderListResult, loading: reminderListLoading } = useSelector(getReminderList);
  const { result: myOrganization } = useSelector(getMyOrganization);
  const myOrganizationId = myOrganization?.id;

  useEffect(() => {
    if (!myOrganizationId) return;
    dispatch(
      getReminderListRequest({
        filter: { organizationId: myOrganizationId },
      })
    );
  }, [dispatch, myOrganizationId]);

  const getTitle = (type: string, recursiveMode: string) => {
    if (type === "abnormal_reading") {
      return recursiveMode === "daily"
        ? "Daily Abnormal Screening Notification"
        : recursiveMode === "hourly"
        ? "Hourly Abnormal Screening Notification"
        : "Abnormal Screening Notification";
    } else if (type === "next_reading") {
      return recursiveMode === "daily"
        ? "Daily Next Screening Notification"
        : "Next Screening Notification";
    }
    return "Notification";
  };

  const getReadableRecursiveMode = (recursiveMode: string) => {
    switch (recursiveMode) {
      case "daily":
        return "Everyday";
      case "hourly":
        return "Every Hour";
      default:
        return "Once";
    }
  };

  // Dedupe reminders by (organizationId, type, recursiveMode). The reminder
  // table has no unique constraint on this combination, so legacy/migrated
  // data can contain duplicates that render as repeated notification cards.
  // Prefer the entry that already has emails configured.
  const dedupedReminders = (() => {
    const items = reminderListResult?.items || [];
    const byKey: Record<string, any> = {};
    for (const item of items) {
      const key = `${item.organizationId || ""}|${item.type || ""}|${item.recursiveMode || ""}`;
      const existing = byKey[key];
      const itemEmails = Array.isArray(item.emails) ? item.emails : [];
      if (!existing) {
        byKey[key] = item;
        continue;
      }
      const existingEmails = Array.isArray(existing.emails) ? existing.emails : [];
      if (itemEmails.length > existingEmails.length) {
        byKey[key] = item;
      }
    }
    return Object.values(byKey);
  })();

  return (
    <div>
      <Spin spinning={reminderListLoading}>
        <div className="notifications-container">
          <div className="notifications-list">
            {dedupedReminders.map((item: any) => {
              const title = getTitle(item?.type || "", item.recursiveMode || "");
              const readableRecursiveMode = getReadableRecursiveMode(item.recursiveMode || "");

              return (
                <NotificationCard
                  key={item.id}
                  title={title}
                  item={item}
                  emails={item.emails || []}
                  schedule={`${readableRecursiveMode} at ${item.hour || '00'}:${item.minute || '00'}`}
                />
              );
            })}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default Notifications;
